import React, { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useBookDemo } from "@/contexts/BookDemoContext";
import { getCurrentPageUrlWithRefFromCookie } from "@/services/cookieService";

const BLOCKED_EMAIL_DOMAINS = new Set([
  "gmail.com",
  "googlemail.com",
  "yopmail.com",
  "yopmail.fr",
  "yopmail.net",
]);

function isBlockedConsumerDomain(email) {
  const at = email.lastIndexOf("@");
  if (at < 0) return false;
  let domain = email
    .slice(at + 1)
    .toLowerCase()
    .trim();
  if (!domain) return false;
  if (BLOCKED_EMAIL_DOMAINS.has(domain)) return true;
  if (domain.includes("yopmail")) return true;
  return false;
}

const ISD_OPTIONS = [
  { value: "+1", label: "US +1" },
  { value: "+44", label: "UK +44" },
  { value: "+91", label: "IN +91" },
  { value: "+971", label: "AE +971" },
  { value: "+61", label: "AU +61" },
  { value: "+65", label: "SG +65" },
  { value: "+49", label: "DE +49" },
  { value: "+33", label: "FR +33" },
  { value: "+81", label: "JP +81" },
  { value: "+82", label: "KR +82" },
  { value: "+86", label: "CN +86" },
  { value: "+60", label: "MY +60" },
];

const formSchema = z.object({
  fullName: z.string().min(2, "Enter your full name"),
  workEmail: z
    .string()
    .email("Enter a valid email")
    .refine((val) => !isBlockedConsumerDomain(val.trim()), {
      message:
        "Please use a work email. Gmail, YOPmail, and similar personal addresses are not accepted.",
    }),
  isd: z.string().min(1),
  phoneLocal: z
    .string()
    .min(5, "Enter your phone number")
    .regex(/^[\d\s\-()]+$/, "Use digits, spaces, or hyphens only"),
  companyName: z.string().min(2, "Enter your company name"),
  trainingRequirements: z
    .string()
    .trim()
    .min(1, "Please share your training requirements"),
  otp: z.string().min(1, "Enter the OTP"),
});

const API_BASE = process.env.REACT_APP_API_URL;

async function parseApiError(res) {
  let message = `Something went wrong (${res.status}). Please try again.`;
  try {
    const json = await res.json();
    if (typeof json?.message === "string" && json.message)
      message = json.message;
    else if (typeof json?.error === "string" && json.error)
      message = json.error;
  } catch {
    // non-JSON body — keep generic message
  }
  return message;
}

const fieldClass =
  "min-h-10 border-slate-200 bg-white text-base text-slate-900 placeholder:text-slate-400 focus-visible:border-indigo-300 focus-visible:ring-indigo-200 sm:min-h-9 sm:text-sm";

export default function BookDemoModal() {
  const { open, setOpen } = useBookDemo();
  const [done, setDone] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [otpSent, setOtpSent] = useState(false);
  const [otpSending, setOtpSending] = useState(false);
  const [otpError, setOtpError] = useState(null);
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [otpVerifyError, setOtpVerifyError] = useState(null);

  const {
    register,
    handleSubmit,
    control,
    reset,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      isd: "+91",
      fullName: "",
      workEmail: "",
      phoneLocal: "",
      companyName: "",
      trainingRequirements: "",
      otp: "",
    },
  });

  const handleSendOtp = async () => {
    setOtpError(null);
    const { isd, phoneLocal } = getValues();
    if (!phoneLocal || phoneLocal.trim().length < 5) {
      setOtpError("Enter your phone number before requesting an OTP.");
      return;
    }
    setOtpSending(true);
    try {
      const res = await fetch(`${API_BASE}/api/v1/users/otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          mobile: phoneLocal.trim(),
          isd,
          type: "preregister",
        }),
      });
      if (!res.ok) throw new Error(await parseApiError(res));
      setOtpSent(true);
    } catch (err) {
      setOtpError(err.message || "Failed to send OTP. Please try again.");
    } finally {
      setOtpSending(false);
    }
  };

  const handleVerifyOtp = async () => {
    setOtpVerifyError(null);
    const { isd, phoneLocal, otp } = getValues();
    if (!otp || !otp.trim()) {
      setOtpVerifyError("Enter the OTP before verifying.");
      return;
    }
    setOtpVerifying(true);
    try {
      const res = await fetch(`${API_BASE}/api/v1/users/validate-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          mobile: phoneLocal.trim(),
          isd,
          otp: otp.trim(),
        }),
      });
      if (!res.ok) throw new Error(await parseApiError(res));
      setOtpVerified(true);
    } catch (err) {
      setOtpVerified(false);
      setOtpVerifyError(err.message || "Invalid OTP. Please try again.");
    } finally {
      setOtpVerifying(false);
    }
  };

  const onSubmit = async (data) => {
    setSubmitError(null);
    try {
      const res = await fetch(
        `${API_BASE}/gravity/v1/public/training-lead`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            name: data.fullName.trim(),
            email: data.workEmail.trim(),
            isd: data.isd,
            mobile: data.phoneLocal.trim(),
            company: data.companyName.trim(),
            message: data.trainingRequirements.trim(),
            otp: data.otp.trim(),
            reference_url: getCurrentPageUrlWithRefFromCookie(),
            verified: otpVerified,
          }),
        },
      );
      if (!res.ok) throw new Error(await parseApiError(res));
    } catch (err) {
      setSubmitError(err.message || "Something went wrong. Please try again.");
      return;
    }
    setDone(true);
    reset();
  };

  useEffect(() => {
    if (!open) {
      setDone(false);
      setSubmitError(null);
      setOtpSent(false);
      setOtpVerified(false);
      setOtpError(null);
      setOtpVerifyError(null);
      reset();
    }
  }, [open, reset]);

  const onOpenChange = (next) => {
    setOpen(next);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={[
          "w-[85vw] max-w-2xl gap-0 p-0 sm:w-[min(100%,calc(100vw-1.25rem))]",
          "max-h-[min(90dvh,880px)] overflow-y-auto overscroll-contain",
          "border-slate-200/90 bg-white text-slate-900",
          "shadow-[0_32px_64px_-24px_rgba(15,23,42,0.22)]",
          "rounded-2xl",
          "[&>button]:right-3 [&>button]:top-3 [&>button]:sm:right-4 [&>button]:sm:top-4",
          "[&>button]:text-slate-500 [&>button]:hover:bg-slate-100 [&>button]:ring-offset-white",
        ].join(" ")}
      >
        <div className="px-4 py-5 sm:px-7 sm:py-8 md:px-10 md:py-9">
          {!done && (
            <DialogHeader className="space-y-1.5 text-left sm:space-y-2">
              <DialogTitle
                className="text-lg font-bold tracking-tight sm:text-xl md:text-2xl bg-clip-text text-transparent"
                style={{
                  backgroundImage:
                    "linear-gradient(135deg, #2563eb 0%, #4f46e5 45%, #7c3aed 100%)",
                  WebkitBackgroundClip: "text",
                  backgroundClip: "text",
                }}
              >
                Book a Demo
              </DialogTitle>
              <DialogDescription className="text-[13.5px] sm:text-[14px] leading-relaxed text-slate-600 text-left">
                See how Sparkl can help your team manage training, track learner
                progress, automate assignments, and improve completion rates.
              </DialogDescription>
            </DialogHeader>
          )}

          {done ? (
            <div className="flex min-h-[220px] flex-col items-center justify-center gap-4 py-10 text-center">
    
              <p className="text-[20px] font-medium leading-relaxed text-slate-800">
                Thank you! Your demo request has been received. Our product
                specialist will contact you shortly.
              </p>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit(onSubmit)}
              className="mt-5 space-y-3.5 sm:mt-6 sm:space-y-4"
              data-testid="book-demo-form"
            >
              <div className="space-y-1.5">
                <Label htmlFor="bd-fullName" className="text-slate-800">
                  Full name
                </Label>
                <Input
                  id="bd-fullName"
                  className={fieldClass}
                  autoComplete="name"
                  {...register("fullName")}
                />
                {errors.fullName && (
                  <p className="text-[12px] text-rose-600">
                    {errors.fullName.message}
                  </p>
                )}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="bd-workEmail" className="text-slate-800">
                  Work email
                </Label>
                <Input
                  id="bd-workEmail"
                  type="email"
                  className={fieldClass}
                  autoComplete="email"
                  placeholder="name@company.com"
                  {...register("workEmail")}
                />
                {errors.workEmail && (
                  <p className="text-[12px] text-rose-600">
                    {errors.workEmail.message}
                  </p>
                )}
                <p className="text-[11px] text-slate-500">
                  Personal inboxes (Gmail, YOPmail, etc.) cannot be used.
                </p>
              </div>

              <div className="space-y-1.5">
                <Label className="text-slate-800">Phone number</Label>
                <div className="flex flex-row items-stretch gap-2">
                  <Controller
                    name="isd"
                    control={control}
                    render={({ field }) => (
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                      >
                        <SelectTrigger
                          className={`h-10 w-[120px] shrink-0 sm:h-9 ${fieldClass} data-[placeholder]:text-slate-400`}
                          id="bd-isd"
                        >
                          <SelectValue placeholder="ISD" />
                        </SelectTrigger>
                        <SelectContent>
                          {ISD_OPTIONS.map((o) => (
                            <SelectItem key={o.value} value={o.value}>
                              {o.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  />
                  <Input
                    id="bd-phone"
                    type="tel"
                    inputMode="tel"
                    className={`min-w-0 flex-1 ${fieldClass}`}
                    autoComplete="tel-national"
                    placeholder="Phone number"
                    {...register("phoneLocal")}
                  />
                  <button
                    type="button"
                    onClick={handleSendOtp}
                    disabled={otpSending || otpSent}
                    className="shrink-0 rounded-lg border border-indigo-300 bg-indigo-50 px-3 text-[12.5px] font-semibold text-indigo-700 transition-colors hover:bg-indigo-100 disabled:opacity-50 disabled:cursor-not-allowed sm:px-4"
                  >
                    {otpSending ? "Sending…" : otpSent ? "Sent ✓" : "Send OTP"}
                  </button>
                </div>
                {(errors.isd || errors.phoneLocal) && (
                  <p className="text-[12px] text-rose-600">
                    {errors.phoneLocal?.message || errors.isd?.message}
                  </p>
                )}
                {otpError && (
                  <p className="text-[12px] text-rose-600">{otpError}</p>
                )}
              </div>

              {otpSent && (
                <div className="space-y-1.5">
                  <Label htmlFor="bd-otp" className="text-slate-800">
                    Enter OTP
                  </Label>
                  <div className="flex items-stretch gap-2">
                    <Input
                      id="bd-otp"
                      type="text"
                      inputMode="numeric"
                      maxLength={8}
                      className={`min-w-0 flex-1 ${fieldClass} ${otpVerified ? "border-emerald-400 bg-emerald-50" : ""}`}
                      placeholder="Enter the OTP sent to your phone"
                      disabled={otpVerified}
                      {...register("otp")}
                    />
                    <button
                      type="button"
                      onClick={handleVerifyOtp}
                      disabled={otpVerifying || otpVerified}
                      className="shrink-0 rounded-lg border border-indigo-300 bg-indigo-50 px-3 text-[12.5px] font-semibold text-indigo-700 transition-colors hover:bg-indigo-100 disabled:opacity-50 disabled:cursor-not-allowed sm:px-4"
                    >
                      {otpVerifying
                        ? "Verifying…"
                        : otpVerified
                          ? "Verified ✓"
                          : "Verify"}
                    </button>
                  </div>
                  {(otpVerifyError || (errors.otp && !otpVerified)) && (
                    <p className="text-[12px] text-rose-600">
                      {otpVerifyError || errors.otp?.message}
                    </p>
                  )}
                  {otpVerified ? (
                    <p className="text-[11px] text-emerald-600 font-medium">
                      OTP verified successfully.
                    </p>
                  ) : (
                    <p className="text-[11px] text-slate-500">
                      Didn&apos;t receive it?{" "}
                      <button
                        type="button"
                        onClick={() => {
                          setOtpSent(false);
                          setOtpVerified(false);
                          setOtpError(null);
                          setOtpVerifyError(null);
                        }}
                        className="text-indigo-600 underline underline-offset-2 hover:text-indigo-800"
                      >
                        Resend OTP
                      </button>
                    </p>
                  )}
                </div>
              )}

              <div className="space-y-1.5">
                <Label htmlFor="bd-companyName" className="text-slate-800">
                  Company name
                </Label>
                <Input
                  id="bd-companyName"
                  className={fieldClass}
                  autoComplete="organization"
                  {...register("companyName")}
                />
                {errors.companyName && (
                  <p className="text-[12px] text-rose-600">
                    {errors.companyName.message}
                  </p>
                )}
              </div>

              <div className="space-y-1.5">
                <Label
                  htmlFor="bd-trainingRequirements"
                  className="text-slate-800"
                >
                  Training requirements 
                </Label>
                <Textarea
                  id="bd-trainingRequirements"
                  rows={3}
                  className={`${fieldClass} min-h-[88px] resize-y text-base sm:text-sm`}
                  placeholder="Share your goals, team size, current challenges, or implementation timeline."
                  {...register("trainingRequirements")}
                />
                {errors.trainingRequirements && (
                  <p className="text-[12px] text-rose-600">
                    {errors.trainingRequirements.message}
                  </p>
                )}
              </div>

              {submitError && (
                <p className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-[12.5px] text-rose-700">
                  {submitError}
                </p>
              )}

              <Button
                type="submit"
                className="mt-2 w-full touch-manipulation rounded-full py-5 text-[15px] font-semibold text-white border-0 shadow-[0_10px_28px_-6px_rgba(79,70,229,0.45)] hover:opacity-[0.95] sm:py-6"
                style={{
                  background:
                    "linear-gradient(135deg, #2563eb 0%, #4f46e5 50%, #7c3aed 100%)",
                }}
                disabled={isSubmitting || !otpVerified}
              >
                {isSubmitting ? "Sending…" : "Request demo"}
              </Button>
            </form>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
